import React from "react";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";

import Layout from "../Layout.tsx";
import Title from "../Title.tsx";
import { readBranches } from "../../services/branch.service.ts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { useFormik } from "formik";
import Branch from "../../models/Branch.ts";
import { useParams } from "react-router-dom";
import { Panel, PanelBody, PanelHeader } from "../panel/panel.jsx";
import IncidentsForm from "./IncidentsForm.tsx";
import IncidentsFormDirectBoss from "./IncidentsFormDirectBoss.tsx";
import { IncidentsDefaultValues } from "../../interfaces/formik/IIncidents.ts";
import Incidents, { type IncidentsTableProps } from "./Incidents.tsx";
import IncidentsDirectBoss from "./IncidentsDirectBoss.tsx";
import { Check, Incident, Unschedule } from "../../models/Incidents.ts";
import {
  findIncidents,
  findIncidentsRange,
  findIncidentsDirectBoss,
  findIncidentsBySchedules,
  findIncidentsBySchedule,
} from "../../services/incident.service.ts";
import type { IncidentAfterSavePayload } from "./incidentTableTypes.ts";
import { findByExactDateAndBranch } from "../../services/branchSchedule.service.ts";
import dayjs from "dayjs";
import ModalContainer from "../ModalContainer.tsx";
import {
  setShowModal,
  setRefetch,
} from "../../store/slices/incidenciaSlice.ts";
import ModalClock from "./ModalClock.tsx";
import { clearProfileImageCacheByCurp } from "../../services/profileImageCache.ts";
import { notification } from "../../utils/Notifications.tsx";
import {
  fetchDirectBossById,
  fetchDirectBosses,
  fetchDirectBossesByBossId,
} from "../../services/direct-boss.service.ts";
import { getUser } from "../../services/user.service.ts";
import { autocompletePartners } from "../../services/partner.service.ts";
import {
  excludePendingLeaderReviewIncidents,
  filterOperationalPendingNoProgramados,
  filterOperationalFalseNoCheckoutIncidents,
  isPendingLeaderReviewIncident,
  isResolvedOperationalIncident,
} from "../../utils/incidentLeaderReviewFilter.ts";
import { loadBranchReportMetaMap } from "../../utils/payrollBranchMeta.ts";
import type { BranchReportMeta } from "../../utils/operationalDayReporteChequeo.ts";
import { collectIncidentBranchIds, buildUnscheduleIncidentRecord } from "../../utils/incidentBranchMeta.ts";
import { resolveIncidentsNightTableContext } from "../../utils/nightScheduleEligibility.ts";
import {
  sortIncidentsByEntryTime,
  sortUnscheduleByEntryTime,
} from "../../utils/incidentSort.ts";

const incidentRowKey = (incident: {
  schedule?: { id?: number };
  status?: string;
}) => `${incident.schedule?.id}-${incident.status}`;

function ensureSchedulePartner(
  partner?: {
    id?: number;
    person?: {
      firstName?: string;
      lastName?: string;
      secondLastName?: string;
    };
    candidate?: { shift?: { name?: string } };
  } | null,
  fallback?: typeof partner
) {
  const source = partner?.person
    ? partner
    : fallback?.person
      ? fallback
      : partner ?? fallback;
  const person = source?.person ?? fallback?.person;
  return {
    ...(source || { id: 0 }),
    person: {
      id: person?.id ?? 0,
      firstName: person?.firstName ?? "",
      lastName: person?.lastName ?? "",
      secondLastName: person?.secondLastName ?? "",
    },
    candidate: source?.candidate ?? fallback?.candidate,
  };
}

function enrichIncidentFromScheduleSource(
  incident: Incident,
  source?: {
    partner?: any;
    positionName?: string;
    shift?: string;
    startTime?: string;
    endTime?: string;
  } | null
): Incident {
  if (!source?.partner?.person || incident.schedule?.partner?.person) {
    return incident;
  }
  return {
    ...incident,
    schedule: {
      ...incident.schedule,
      partner: ensureSchedulePartner(
        incident.schedule.partner,
        source.partner
      ) as Incident["schedule"]["partner"],
      positionName: incident.schedule.positionName || source.positionName || "",
      shift: incident.schedule.shift || source.shift || "",
      startTime: incident.schedule.startTime || source.startTime || "",
      endTime: incident.schedule.endTime || source.endTime || "",
    },
  };
}

function enrichIncidentsFromProgramados(
  incidents: Incident[],
  programados: any[]
): Incident[] {
  if (!programados?.length) return incidents;
  const byId = new Map<number, any>();
  for (const row of programados) {
    if (row?.id) {
      byId.set(row.id, row);
    }
  }
  return incidents.map((inc) =>
    enrichIncidentFromScheduleSource(inc, byId.get(Number(inc.schedule?.id)))
  );
}

const mergeIncidentsByKey = (...lists: Incident[][]): Incident[] => {
  const byKey = new Map<string, Incident>();
  for (const list of lists) {
    for (const incident of list) {
      const key = incidentRowKey(incident);
      if (!incident.schedule?.id || !incident.status) continue;
      byKey.set(key, incident);
    }
  }
  return [...byKey.values()];
};

type IncidentsRawPayload = {
  filteredIncidents: Incident[];
  filteredUnschedule: Unschedule[];
  savedResolvedIncidents: Incident[];
  savedOpenIncidents: Incident[];
  dayClose?: any;
};

const applyIncidentViewMode = (
  raw: IncidentsRawPayload,
  incidentViewMode: "pending" | "resolved" | "all"
): { records: Incident[]; unschedule: Unschedule[] } => {
  let finalIncidents: Incident[] = [];
  let finalUnschedule: Unschedule[] = [];

  if (incidentViewMode === "pending") {
    finalIncidents = mergeIncidentsByKey(
      raw.filteredIncidents,
      raw.savedOpenIncidents
    );
    finalUnschedule = raw.filteredUnschedule;
  } else if (incidentViewMode === "resolved") {
    finalIncidents = raw.savedResolvedIncidents;
    finalUnschedule = [];
  } else {
    const resolvedKeys = new Set(
      raw.savedResolvedIncidents.map((inc) => incidentRowKey(inc))
    );
    const livePending = raw.filteredIncidents.filter(
      (inc) => !resolvedKeys.has(incidentRowKey(inc))
    );
    finalIncidents = mergeIncidentsByKey(
      livePending,
      raw.savedOpenIncidents,
      raw.savedResolvedIncidents
    );
    finalUnschedule = raw.filteredUnschedule;
  }

  return {
    records: sortIncidentsByEntryTime(
      excludePendingLeaderReviewIncidents(finalIncidents)
    ),
    unschedule: sortUnscheduleByEntryTime(finalUnschedule),
  };
};

const buildIncidentsRawCacheKey = (params: {
  isDirectBossAccess: boolean;
  bossId?: string;
  directBossId?: number;
  branch: number;
  date: string;
  partnerId: number | null;
  dateRange?: { startDate: number; endDate: number };
}): string => {
  const {
    isDirectBossAccess,
    bossId,
    directBossId,
    branch,
    date,
    partnerId,
    dateRange,
  } = params;
  const hasRange = Boolean(dateRange?.startDate && dateRange?.endDate);
  if (isDirectBossAccess) {
    return `directboss-${directBossId || bossId || "none"}-${
      hasRange && dateRange
        ? `${dateRange.startDate}-${dateRange.endDate}`
        : date
    }-${partnerId || "all"}`;
  }
  if (hasRange && dateRange) {
    return `${branch}-${dateRange.startDate}-${dateRange.endDate}-${
      partnerId || "all"
    }`;
  }
  return `${branch}-${date}-${partnerId || "all"}`;
};

function canFetchIncidentsValues(
  values: { branch?: number; date?: string },
  isDirectBossAccess: boolean,
  bossId?: string
): boolean {
  if (!values.date) {
    return false;
  }
  if (isDirectBossAccess) {
    return Boolean(bossId);
  }
  const branch = Number(values.branch);
  return Number.isFinite(branch) && branch !== -99;
}

function buildScheduleLookupFromProgramados(programadosData: any[]): Map<number, any> {
  const lookup = new Map<number, any>();
  (programadosData || []).forEach((schedule: any) => {
    if (!schedule?.id) {
      return;
    }
    lookup.set(schedule.id, {
      ...schedule,
      checkIn: schedule.checkIn ?? schedule.assistanceCheck ?? null,
      checkOut: schedule.checkOut ?? schedule.assistanceCheckout ?? null,
    });
  });
  return lookup;
}

function enrichScheduleLookupWithSaved(
  lookup: Map<number, any>,
  savedRows: any[] | undefined
) {
  for (const saved of savedRows || []) {
    const scheduleId = Number(saved?.schedule?.id);
    if (!scheduleId) {
      continue;
    }
    const schedule = saved.schedule ?? {};
    const existing = lookup.get(scheduleId);
    lookup.set(scheduleId, {
      ...(existing ?? schedule),
      ...schedule,
      checkIn:
        saved.checkIn ??
        schedule.assistanceCheck ??
        schedule.checkIn ??
        existing?.checkIn ??
        existing?.assistanceCheck ??
        null,
      checkOut:
        saved.checkOut ??
        schedule.assistanceCheckout ??
        schedule.checkOut ??
        existing?.checkOut ??
        existing?.assistanceCheckout ??
        null,
      checks:
        (Array.isArray(schedule.checks) && schedule.checks.length > 0
          ? schedule.checks
          : null) ??
        (Array.isArray(existing?.checks) && existing.checks.length > 0
          ? existing.checks
          : []),
    });
  }
}

function mapSavedRowsToTableIncidents(
  savedRows: any[] | undefined,
  scheduleLookup: Map<number, any>
): Incident[] {
  return (savedRows || [])
    .map((savedInc: any) => {
      const scheduleId = Number(savedInc?.schedule?.id);
      const schedule =
        scheduleLookup.get(scheduleId) ?? savedInc?.schedule ?? null;
      return mapSavedIncidentToTableRow(savedInc, schedule);
    })
    .filter((inc): inc is Incident => inc !== null);
}

function mergeSavedIncidentsFromApi(
  base: any[] | undefined,
  extra: any[] | undefined
): any[] | undefined {
  if (extra === undefined) {
    return base;
  }
  const combined = [...(base ?? []), ...extra];
  const byKey = new Map<string, any>();
  for (const row of combined) {
    const incidentId = Number(row?.id);
    const scheduleId = Number(row?.schedule?.id);
    const status = String(row?.status ?? "");
    const key =
      incidentId > 0 ? `id:${incidentId}` : `s:${scheduleId}:${status}`;
    if (scheduleId && status) {
      byKey.set(key, row);
    }
  }
  return [...byKey.values()];
}

async function enrichSavedRowsWithScheduleChecks(
  savedRows: any[] | undefined,
  options: {
    branchId: number;
    startDate: string;
    endDate: string;
    partnerId?: number | null;
  }
): Promise<any[]> {
  if (!savedRows?.length) {
    return savedRows ?? [];
  }

  const needsCheckEnrichment = savedRows.some((row) => {
    const scheduleId = Number(row?.schedule?.id);
    if (!scheduleId) {
      return false;
    }
    const hasTopCheck =
      row?.checkIn?.checkTime || row?.schedule?.assistanceCheck?.checkTime;
    const hasChecks = Array.isArray(row?.schedule?.checks) && row.schedule.checks.length > 0;
    return !hasTopCheck && !hasChecks;
  });

  if (!needsCheckEnrichment) {
    return savedRows;
  }

  const scheduleIds = [
    ...new Set(
      savedRows
        .map((row) => Number(row?.schedule?.id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ),
  ];
  if (!scheduleIds.length) {
    return savedRows;
  }

  try {
    const enriched = await findIncidentsBySchedules(scheduleIds, {
      branchId: options.branchId,
      startDate: options.startDate,
      endDate: options.endDate,
      ...(options.partnerId ? { partnerId: options.partnerId } : {}),
    });
    const byKey = new Map<string, any>();
    for (const row of enriched || []) {
      const scheduleId = Number(row?.schedule?.id);
      const status = String(row?.status ?? "");
      if (scheduleId && status) {
        byKey.set(`${scheduleId}::${status}`, row);
      }
    }

    return savedRows.map((row) => {
      const scheduleId = Number(row?.schedule?.id);
      const status = String(row?.status ?? "");
      const full = byKey.get(`${scheduleId}::${status}`);
      if (!full) {
        return row;
      }
      return {
        ...row,
        checkIn: full.checkIn ?? row.checkIn,
        checkOut: full.checkOut ?? row.checkOut,
        schedule: {
          ...row.schedule,
          ...full.schedule,
          startTime: row.schedule?.startTime || full.schedule?.startTime,
          endTime: row.schedule?.endTime || full.schedule?.endTime,
          assistanceCheck:
            full.schedule?.assistanceCheck ?? row.schedule?.assistanceCheck,
          assistanceCheckout:
            full.schedule?.assistanceCheckout ??
            row.schedule?.assistanceCheckout,
          checks: full.schedule?.checks ?? row.schedule?.checks ?? [],
        },
      };
    });
  } catch (error) {
    console.error("Error al enriquecer incidencias guardadas con checadas:", error);
    return savedRows;
  }
}

const mapSavedIncidentToTableRow = (
  savedInc: any,
  schedule: any
): Incident | null => {
  if (!schedule?.id) {
    return null;
  }

  const incident: Incident = {
    schedule: {
      id: schedule.id,
      positionName: schedule.positionName || "",
      startTime: schedule.startTime || "",
      endTime: schedule.endTime || "",
      exactDate: schedule.exactDate,
      assistanceStatus: savedInc.status,
      isReplacement: schedule.isReplacement || false,
      shift: schedule.shift || "",
      positionX: schedule.positionX,
      positionY: schedule.positionY,
      unscheduled: schedule.unscheduled,
      partner: ensureSchedulePartner(
        schedule.partner,
        savedInc?.schedule?.partner
      ) as Incident["schedule"]["partner"],
      checkIn:
        savedInc.checkIn ??
        schedule.checkIn ??
        schedule.assistanceCheck ??
        null,
      checkOut:
        savedInc.checkOut ??
        schedule.checkOut ??
        schedule.assistanceCheckout ??
        null,
      checks:
        savedInc.schedule?.checks ??
        schedule.checks ??
        [],
      branchId:
        schedule.branchId ??
        schedule.branch?.id ??
        savedInc.schedule?.branchId ??
        savedInc.schedule?.branch?.id,
      branch: schedule.branch ?? savedInc.schedule?.branch,
    },
    checkIn:
      savedInc.checkIn ?? schedule.checkIn ?? schedule.assistanceCheck ?? null,
    checkOut:
      savedInc.checkOut ??
      schedule.checkOut ??
      schedule.assistanceCheckout ??
      null,
    diff: 0,
    status: savedInc.status,
    ready: true,
    isCodeChecked: false,
  };

  const isResolved = savedInc.finalStatus === true;
  (incident as any).resolved = isResolved;
  (incident as any).incidentId = savedInc.id;
  (incident as any).reason = savedInc.reason;
  (incident as any).type = savedInc.type;
  (incident as any).finalStatus = savedInc.finalStatus;
  (incident as any).createdAt = savedInc.createdAt;
  (incident as any).updateAt = savedInc.updateAt;
  (incident as any).authorizeBy = savedInc.authorizeBy || null;
  (incident as any).isPendingLeaderReview =
    savedInc.isPendingLeaderReview === true ||
    (savedInc.finalStatus == null && !savedInc.authorizeBy);

  return incident;
};

const normalizeAfterSavePayload = (
  payload?: IncidentAfterSavePayload | number
): IncidentAfterSavePayload | null => {
  if (payload == null) return null;
  if (typeof payload === "number") {
    return {
      kind: "schedule",
      scheduleId: payload,
      status: "",
      record: null as unknown as Incident,
    };
  }
  return payload;
};

const mapLivePendingToTableRow = (
  live: any,
  fallback?: Incident | null
): Incident | null => {
  const schedule = live?.schedule;
  const status = live?.status as string | undefined;
  if (!schedule?.id || !status) return null;

  const base = fallback?.schedule;
  const partner = ensureSchedulePartner(
    schedule.partner?.person != null ? schedule.partner : null,
    base?.partner ?? schedule.partner
  );

  return {
    schedule: {
      id: schedule.id,
      positionName: schedule.positionName || base?.positionName || "",
      startTime: schedule.startTime || base?.startTime || "",
      endTime: schedule.endTime || base?.endTime || "",
      exactDate: schedule.exactDate ?? base?.exactDate,
      assistanceStatus: status,
      isReplacement: schedule.isReplacement ?? base?.isReplacement ?? false,
      shift: schedule.shift || base?.shift || "",
      partner,
      checkIn:
        live.checkIn ??
        schedule.checkIn ??
        schedule.assistanceCheck ??
        base?.checkIn ??
        null,
      checkOut:
        live.checkOut ??
        schedule.checkOut ??
        schedule.assistanceCheckout ??
        base?.checkOut ??
        null,
      checks: schedule.checks || base?.checks || [],
      branchId:
        schedule.branchId ??
        schedule.branch?.id ??
        base?.branchId ??
        base?.branch?.id,
      branch: schedule.branch ?? base?.branch,
    },
    checkIn: live.checkIn ?? schedule.checkIn ?? schedule.assistanceCheck ?? null,
    checkOut:
      live.checkOut ?? schedule.checkOut ?? schedule.assistanceCheckout ?? null,
    diff: 0,
    status,
    ready: true,
    isCodeChecked: Boolean(live.isCodeChecked),
  };
};

async function buildScheduleLookupForBranchDay(
  branchId: number,
  startDate: string,
  endDate: string,
  programadosData: any[]
): Promise<Map<number, any>> {
  const lookup = new Map<number, any>();
  (programadosData || []).forEach((schedule) => {
    if (schedule?.id) {
      lookup.set(schedule.id, schedule);
    }
  });

  const start = dayjs(startDate, "YYYY-MM-DD");
  const end = dayjs(endDate, "YYYY-MM-DD");
  if (!start.isValid() || !end.isValid()) {
    return lookup;
  }

  const mergeDaySchedules = async (dateStr: string) => {
    try {
      const daySchedules = await findByExactDateAndBranch(dateStr, branchId);
      (Array.isArray(daySchedules) ? daySchedules : []).forEach(
        (schedule: any) => {
          if (!schedule?.id) {
            return;
          }
          lookup.set(schedule.id, {
            ...schedule,
            checkIn: schedule.checkIn ?? schedule.assistanceCheck ?? null,
            checkOut: schedule.checkOut ?? schedule.assistanceCheckout ?? null,
          });
        }
      );
    } catch (error) {
      console.warn(
        "No se pudieron cargar horarios del día para incidencias guardadas:",
        dateStr,
        error
      );
    }
  };

  if (start.isSame(end, "day")) {
    await mergeDaySchedules(start.format("YYYY-MM-DD"));
    return lookup;
  }

  for (
    let cursor = start.clone();
    !cursor.isAfter(end, "day");
    cursor = cursor.add(1, "day")
  ) {
    await mergeDaySchedules(cursor.format("YYYY-MM-DD"));
  }

  return lookup;
}

const TableIncidents = ({
  forceDirectBoss = false,
}: {
  forceDirectBoss?: boolean;
} = {}) => {
  useAppSettings();
  const { id, bossId } = useParams();
  const dispatch = useDispatch();
  const [branchs, setBranchs] = React.useState([]);
  const [title, setTitle] = React.useState("Incidencias del día");
  const [records, setRecords] = React.useState<Incident[]>([]);
  const [unschedule, setUnschedule] = React.useState<Unschedule[]>([]);
  const [dayClose, setDayClose] = React.useState<{
    id?: number;
    branchId?: number;
    date?: string;
    closedAt?: string | Date;
    closedBy?: {
      id?: number;
      name?: string;
      lastName?: string;
      secondLastName?: string;
    } | null;
  } | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [hidePlannedColumns, setHidePlannedColumns] = React.useState(false);
  const [branchMetaMap, setBranchMetaMap] = React.useState<
    Record<number, BranchReportMeta>
  >({});
  const [directBossEmployees, setDirectBossEmployees] = React.useState<any[]>(
    []
  );
  const cacheRef = React.useRef<
    Map<string, { records: Incident[]; unschedule: Unschedule[] }>
  >(new Map());
  const rawCacheRef = React.useRef<Map<string, IncidentsRawPayload>>(new Map());
  const pendingRequestsRef = React.useRef<
    Map<string, Promise<{ records: Incident[]; unschedule: Unschedule[] }>>
  >(new Map());
  const { user, partner, isAdmin } = useSelector(
    (state: RootState) => state.auth
  );
  const { showModal, refetch } = useSelector(
    (state: RootState) => state.incidencias
  );
  const isDirectBossAccess = forceDirectBoss || Boolean(bossId);
  const branchNameById = React.useMemo(() => {
    const map: Record<number, string> = {};
    for (const branch of branchs as Branch[]) {
      const branchId = Number(branch?.id);
      const name = String(branch?.name ?? "").trim();
      if (branchId > 0 && name) {
        map[branchId] = name;
      }
    }
    return map;
  }, [branchs]);
  const lastPartnerIdRef = React.useRef<any>(null);
  const isInitialDateRef = React.useRef(true);
  const prevIncidentViewModeRef = React.useRef<string | undefined>(undefined);
  const leaderDirectBossPartnerIdRef = React.useRef<number | null>(null);
  const leaderDirectBossEmployeeIdsRef = React.useRef<number[]>([]);
  const leaderBlockedEmployeeIdsRef = React.useRef<number[]>([]);

  const normalizeText = React.useCallback((value?: string | null) => {
    return (value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  }, []);

  const resolveLeaderPartnerId = React.useCallback(
    async (
      leaderBranchId: number
    ): Promise<{ partnerId: number | null; matchedByName: boolean }> => {
      if (partner?.id) {
        return { partnerId: Number(partner.id), matchedByName: false };
      }

      if (user?.id) {
        try {
          const userData = await getUser(Number(user.id));
          if (userData?.partner?.id) {
            return {
              partnerId: Number(userData.partner.id),
              matchedByName: false,
            };
          }
        } catch (error) {
          console.log("No se pudo obtener user.partner desde getUser:", error);
        }
      }

      const firstName = normalizeText(user?.name);
      const lastName = normalizeText(user?.lastName);
      const secondLastName = normalizeText(user?.secondLastName);
      const fullName = [firstName, lastName, secondLastName]
        .filter(Boolean)
        .join(" ")
        .trim();

      if (!fullName || !leaderBranchId) {
        return { partnerId: null, matchedByName: false };
      }

      try {
        const candidates = await autocompletePartners(fullName, leaderBranchId);
        const exactMatches = (Array.isArray(candidates) ? candidates : []).filter(
          (candidate: any) => {
            const cFirstName = normalizeText(candidate?.person?.firstName);
            const cLastName = normalizeText(candidate?.person?.lastName);
            const cSecondLastName = normalizeText(
              candidate?.person?.secondLastName
            );
            const cFullName = [cFirstName, cLastName, cSecondLastName]
              .filter(Boolean)
              .join(" ")
              .trim();
            const cBranchId = Number(candidate?.candidate?.branch?.id);
            return cFullName === fullName && cBranchId === Number(leaderBranchId);
          }
        );

        if (exactMatches.length === 1) {
          return {
            partnerId: Number(exactMatches[0].id),
            matchedByName: true,
          };
        }

        if (exactMatches.length > 1) {
          console.warn(
            "Se encontraron múltiples partners para líder por coincidencia de nombre/sucursal, no se autoasigna:",
            {
              fullName,
              leaderBranchId,
              candidates: exactMatches.map((m: any) => m?.id),
            }
          );
        }
      } catch (error) {
        console.log("No se pudo resolver partner del líder por nombre:", error);
      }

      return { partnerId: null, matchedByName: false };
    },
    [partner?.id, user?.id, user?.name, user?.lastName, user?.secondLastName, normalizeText]
  );

  const calculateBlockedEmployeesForLeader = React.useCallback(
    async (currentLeaderPartnerId?: number | null, currentLeaderEmployeeIds?: number[]) => {
      try {
        const allDirectBoss = await fetchDirectBosses(
          `?limit=500&offset=0&isActive=true`
        );
        const allRows = allDirectBoss?.results || [];
        const assignedToCurrent = new Set(
          (currentLeaderEmployeeIds || [])
            .map((empId) => Number(empId))
            .filter((id) => Boolean(id))
        );
        const assignedToOthers = new Set<number>();

        allRows.forEach((row: any) => {
          const rowBossId = Number(row?.boss?.id);
          if (
            currentLeaderPartnerId &&
            rowBossId &&
            rowBossId === Number(currentLeaderPartnerId)
          ) {
            return;
          }
          (row?.employees || []).forEach((emp: any) => {
            const empId = Number(emp?.id);
            if (empId) assignedToOthers.add(empId);
          });
        });

        return Array.from(assignedToOthers).filter(
          (empId) => !assignedToCurrent.has(empId)
        );
      } catch (error) {
        console.log(
          "No se pudo calcular exclusividad de empleados por jefe directo:",
          error
        );
        return [];
      }
    },
    []
  );

  const fetchIncidentsData = React.useCallback(
    async (
      branch: number,
      date: string,
      partnerId: number | null,
      dateRange?: { startDate: number; endDate: number },
      showLoading: boolean = true,
      directBossId?: number,
      incidentViewMode: "pending" | "resolved" | "all" = "pending",
      forceRefresh: boolean = false
    ): Promise<{ records: Incident[]; unschedule: Unschedule[] }> => {
      const hasRange = Boolean(dateRange?.startDate && dateRange?.endDate);
      const rawKey = buildIncidentsRawCacheKey({
        isDirectBossAccess,
        bossId,
        directBossId,
        branch,
        date,
        partnerId,
        dateRange,
      });
      const cacheKey = `${rawKey}-mode:${incidentViewMode}`;

      if (forceRefresh) {
        rawCacheRef.current.delete(rawKey);
        pendingRequestsRef.current.delete(rawKey);
        for (const key of cacheRef.current.keys()) {
          if (key.startsWith(rawKey)) {
            cacheRef.current.delete(key);
          }
        }
      }

      const rawCached = rawCacheRef.current.get(rawKey);
      if (rawCached) {
        const applied = {
          ...applyIncidentViewMode(rawCached, incidentViewMode),
          dayClose: rawCached.dayClose || null,
        };
        cacheRef.current.set(cacheKey, applied);
        if (showLoading) setLoading(false);
        return applied;
      }

      const pendingRequest = pendingRequestsRef.current.get(rawKey);
      if (pendingRequest) {
        if (showLoading) setLoading(true);
        try {
          const result = await pendingRequest;
          if (showLoading) setLoading(false);
          return result;
        } catch (error) {
          if (showLoading) setLoading(false);
          throw error;
        }
      }

      if (showLoading) setLoading(true);

      const promise = (async () => {
        try {
          let incidents: Incident[] = [];
          let noProgramados: Unschedule[] = [];
          let programadosData: any[] = []; // Guardar programados para usar en incidentViewMode
          let savedIncidentsFromApi: any[] | undefined;
          let dayCloseFromApi: any = null;

          if (isDirectBossAccess) {
            const boss = directBossId || (bossId ? Number(bossId) : undefined);
            const targetDate =
              hasRange && dateRange
                ? dayjs.unix(dateRange.startDate).format("YYYY-MM-DD")
                : date;
            if (!boss) throw new Error("No bossId provided");
            const data = await findIncidentsDirectBoss(boss, targetDate);
            savedIncidentsFromApi = data.savedIncidents;
            dayCloseFromApi = data.dayClose || null;

            let baseIncidents: any[] = data.incidencias || [];
            if (partnerId) {
              baseIncidents = baseIncidents.filter(
                (rec: any) => rec?.schedule?.partner?.id === partnerId
              );
            }
            incidents = baseIncidents as any;
            noProgramados = partnerId
              ? (data.noProgramados || []).filter(
                  (rec: any) => rec?.partner?.id === partnerId
                )
              : data.noProgramados || [];
            programadosData = data.programados || [];
          } else if (hasRange) {
            const start = dayjs.unix(dateRange!.startDate).format("YYYY-MM-DD");
            const end = dayjs.unix(dateRange!.endDate).format("YYYY-MM-DD");
            const data = await findIncidentsRange(
              branch,
              start,
              end,
              partnerId ?? null
            );

            let rangeIncidents: any[] = data.incidencias || [];

            rangeIncidents = rangeIncidents.filter((incident) => {
              const scheduleStatus = incident?.schedule?.assistanceStatus;
              const incidentStatus = incident?.status;
              if (
                scheduleStatus === "Día libre gozado" &&
                incidentStatus === "Día libre gozado"
              ) {
                return false;
              }
              if (!incident?.schedule?.id || !incidentStatus) return false;

              return true;
            });

            incidents = rangeIncidents as any;
            noProgramados = data.noProgramados || [];
            programadosData = data.programados || [];
          } else {
            const data = await findIncidents(branch, date);
            savedIncidentsFromApi = data.savedIncidents;
            dayCloseFromApi = data.dayClose || null;

            let baseIncidents: any[] = partnerId
              ? (data.incidencias || []).filter(
                  (rec: any) => rec?.schedule?.partner?.id === partnerId
                )
              : data.incidencias || [];

            baseIncidents = baseIncidents.filter((incident) => {
              const scheduleStatus = incident?.schedule?.assistanceStatus;
              const incidentStatus = incident?.status;

              if (
                scheduleStatus === "Día libre gozado" &&
                incidentStatus === "Día libre gozado"
              ) {
                return false;
              }

              if (!incident?.schedule?.id || !incidentStatus) return false;

              return true;
            });

            incidents = baseIncidents as any;

            noProgramados = partnerId
              ? (data.noProgramados || []).filter(
                  (rec: any) => rec?.partner?.id === partnerId
                )
              : data.noProgramados;
            programadosData = data.programados || [];
          }

          // Exclusividad por jefe directo:
          // si un colaborador está asignado a otro jefe y NO al líder actual,
          // no debe mostrarse en la vista de sucursal del líder.
          if (!isDirectBossAccess && user?.rol === "Lider de sucursal") {
            const blockedIds = new Set(
              (leaderBlockedEmployeeIdsRef.current || []).map((id) => Number(id))
            );
            if (blockedIds.size > 0) {
              incidents = (incidents || []).filter((inc: any) => {
                const currentPartnerId = Number(inc?.schedule?.partner?.id);
                if (!currentPartnerId) return false;
                return !blockedIds.has(currentPartnerId);
              });
              noProgramados = (noProgramados || []).filter((np: any) => {
                const currentPartnerId = Number(np?.partner?.id);
                if (!currentPartnerId) return false;
                return !blockedIds.has(currentPartnerId);
              });
            }
          }

          // Si el líder también es jefe directo:
          // - Si tiene asignados de su misma sucursal: ver SOLO asignados (todas sucursales).
          // - Si no tiene asignados de su sucursal: ver su sucursal + asignados externos.
          if (!isDirectBossAccess && leaderDirectBossPartnerIdRef.current) {
            try {
              const targetDate = hasRange && dateRange
                ? dayjs.unix(dateRange.startDate).format("YYYY-MM-DD")
                : date;
              const bossData = await findIncidentsDirectBoss(
                leaderDirectBossPartnerIdRef.current,
                targetDate
              );
              savedIncidentsFromApi = mergeSavedIncidentsFromApi(
                savedIncidentsFromApi,
                bossData.savedIncidents
              );
              const assignedIds = new Set(
                (leaderDirectBossEmployeeIdsRef.current || []).map((id) =>
                  Number(id)
                )
              );
              const hasAssignedEmployees = assignedIds.size > 0;

              const bossFiltered = (bossData.incidencias || [])
                .filter((inc: any) => {
                  const scheduleStatus = inc?.schedule?.assistanceStatus;
                  const incidentStatus = inc?.status;
                  if (scheduleStatus === "Día libre gozado" && incidentStatus === "Día libre gozado") return false;
                  if (!inc?.schedule?.id || !incidentStatus) return false;
                  return true;
                })
                .map((inc: any) => ({ ...inc, _fromDirectBoss: true }));

              if (hasAssignedEmployees) {
                const assignedIncidents = bossFiltered.filter((inc: any) => {
                  const currentPartnerId = Number(inc?.schedule?.partner?.id);
                  return currentPartnerId && assignedIds.has(currentPartnerId);
                });
                const assignedNoProgramados = (bossData.noProgramados || []).filter(
                  (np: any) => {
                    const currentPartnerId = Number(np?.partner?.id);
                    return currentPartnerId && assignedIds.has(currentPartnerId);
                  }
                );
                const assignedProgramados = (bossData.programados || []).filter(
                  (p: any) => {
                    const currentPartnerId = Number(p?.partner?.id);
                    return currentPartnerId && assignedIds.has(currentPartnerId);
                  }
                );

                incidents = [...incidents, ...assignedIncidents];
                noProgramados = [
                  ...(Array.isArray(noProgramados) ? noProgramados : []),
                  ...assignedNoProgramados,
                ];
                programadosData = [...programadosData, ...assignedProgramados];
              } else {
                // Si no hay asignaciones activas, conservar comportamiento por sucursal.
                incidents = [...incidents, ...bossFiltered];
                noProgramados = [
                  ...(Array.isArray(noProgramados) ? noProgramados : []),
                  ...(bossData.noProgramados || []),
                ];
                programadosData = [...programadosData, ...(bossData.programados || [])];
              }
            } catch (err) {
              console.log("Error al obtener incidencias de jefe directo para líder:", err);
            }
          }

          const validIncidents = incidents.filter((incident) => {
            return incident && incident.status && incident.schedule?.id;
          });

          const seenIncidentKeys = new Set<string>();
          const filteredIncidents = validIncidents.filter((incident) => {
            const scheduleId = incident.schedule?.id;
            const status = incident.status;
            if (!scheduleId || !status) return false;

            const uniqueKey = `${scheduleId}-${status}`;
            if (seenIncidentKeys.has(uniqueKey)) {
              console.log("🚫 Duplicado exacto eliminado:", {
                scheduleId,
                status,
                uniqueKey,
              });
              return false;
            }
            seenIncidentKeys.add(uniqueKey);
            return true;
          });

          const enrichedFilteredIncidents = filterOperationalFalseNoCheckoutIncidents(
            enrichIncidentsFromProgramados(
              filteredIncidents,
              programadosData || []
            )
          );

          const validUnschedule = noProgramados.filter((item) => {
            return item && item.partner?.id && item.checkIn?.id;
          });

          const seenUnscheduleKeys = new Set<string>();
          const filteredUnschedule = validUnschedule.filter((item) => {
            const partnerId = item.partner?.id;
            const checkInId = item.checkIn?.id;
            if (!partnerId || !checkInId) return false;

            const uniqueKey = `${partnerId}-${checkInId}`;
            if (seenUnscheduleKeys.has(uniqueKey)) {
              return false;
            }
            seenUnscheduleKeys.add(uniqueKey);
            return true;
          });

          let savedResolvedIncidents: Incident[] = [];
          let savedOpenIncidents: Incident[] = [];

          const queryStart = hasRange
            ? dayjs.unix(dateRange!.startDate).format("YYYY-MM-DD")
            : date;
          const queryEnd = hasRange
            ? dayjs.unix(dateRange!.endDate).format("YYYY-MM-DD")
            : date;

          try {
            const scheduleLookup = isDirectBossAccess
              ? buildScheduleLookupFromProgramados(programadosData || [])
              : await buildScheduleLookupForBranchDay(
                  branch,
                  queryStart,
                  queryEnd,
                  programadosData || []
                );
            const allScheduleIds = [...scheduleLookup.keys()];

            enrichScheduleLookupWithSaved(scheduleLookup, savedIncidentsFromApi);

            if (savedIncidentsFromApi !== undefined) {
              let savedRows = savedIncidentsFromApi;
              if (savedRows.length === 0 && allScheduleIds.length > 0) {
                savedRows = await findIncidentsBySchedules(allScheduleIds, {
                  branchId: branch,
                  startDate: queryStart,
                  endDate: queryEnd,
                  ...(partnerId ? { partnerId } : {}),
                });
              } else {
                savedRows = await enrichSavedRowsWithScheduleChecks(savedRows, {
                  branchId: branch,
                  startDate: queryStart,
                  endDate: queryEnd,
                  partnerId,
                });
              }

              enrichScheduleLookupWithSaved(scheduleLookup, savedRows);

              const savedMapped = mapSavedRowsToTableIncidents(
                savedRows,
                scheduleLookup
              );

              savedResolvedIncidents = savedMapped.filter(
                (inc) =>
                  isResolvedOperationalIncident(inc) &&
                  !isPendingLeaderReviewIncident(inc)
              );
              savedOpenIncidents = savedMapped.filter(
                (inc) =>
                  !isResolvedOperationalIncident(inc) &&
                  !isPendingLeaderReviewIncident(inc)
              );
            } else if (allScheduleIds.length > 0) {
              const savedIncidents = await findIncidentsBySchedules(
                allScheduleIds,
                {
                  branchId: branch,
                  startDate: queryStart,
                  endDate: queryEnd,
                  ...(partnerId ? { partnerId } : {}),
                }
              );

              enrichScheduleLookupWithSaved(scheduleLookup, savedIncidents);

              const savedMapped = mapSavedRowsToTableIncidents(
                savedIncidents,
                scheduleLookup
              );

              savedResolvedIncidents = savedMapped.filter(
                (inc) =>
                  isResolvedOperationalIncident(inc) &&
                  !isPendingLeaderReviewIncident(inc)
              );
              savedOpenIncidents = savedMapped.filter(
                (inc) =>
                  !isResolvedOperationalIncident(inc) &&
                  !isPendingLeaderReviewIncident(inc)
              );
            }
          } catch (error) {
            console.error("Error al obtener incidencias guardadas:", error);
            savedResolvedIncidents = [];
            savedOpenIncidents = [];
          }

          const filteredUnschedulePending = filterOperationalPendingNoProgramados(
            filteredUnschedule,
            [...savedResolvedIncidents, ...savedOpenIncidents]
          );

          const enrichedSavedResolved = enrichIncidentsFromProgramados(
            savedResolvedIncidents,
            programadosData || []
          );
          const enrichedSavedOpen = filterOperationalFalseNoCheckoutIncidents(
            enrichIncidentsFromProgramados(
              savedOpenIncidents,
              programadosData || []
            )
          );

          const rawPayload: IncidentsRawPayload = {
            filteredIncidents: enrichedFilteredIncidents,
            filteredUnschedule: filteredUnschedulePending,
            savedResolvedIncidents: enrichedSavedResolved,
            savedOpenIncidents: enrichedSavedOpen,
            dayClose: dayCloseFromApi,
          };
          rawCacheRef.current.set(rawKey, rawPayload);

          const result = {
            ...applyIncidentViewMode(rawPayload, incidentViewMode),
            dayClose: dayCloseFromApi,
          };

          cacheRef.current.set(cacheKey, result);
          if (showLoading) setLoading(false);
          return result;
        } catch (error) {
          console.log(error);
          if (showLoading) setLoading(false);
          return { records: [], unschedule: [], dayClose: null };
        } finally {
          pendingRequestsRef.current.delete(rawKey);
        }
      })();

      pendingRequestsRef.current.set(rawKey, promise);
      return promise;
    },
    [isDirectBossAccess, bossId, user?.rol]
  );

  const clearIncidentsCache = React.useCallback(() => {
    cacheRef.current.clear();
    rawCacheRef.current.clear();
    pendingRequestsRef.current.clear();
  }, []);

  const invalidateIncidentsCache = React.useCallback(
    (
      branch: number,
      date: string,
      partnerId: number | null,
      dateRange?: { startDate: number; endDate: number },
      directBossId?: number
    ) => {
      const rawKey = buildIncidentsRawCacheKey({
        isDirectBossAccess,
        bossId,
        directBossId,
        branch,
        date,
        partnerId,
        dateRange,
      });
      rawCacheRef.current.delete(rawKey);
      pendingRequestsRef.current.delete(rawKey);
      for (const key of cacheRef.current.keys()) {
        if (key.startsWith(rawKey)) {
          cacheRef.current.delete(key);
        }
      }
    },
    [isDirectBossAccess, bossId]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: IncidentsDefaultValues(),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { branch, date, partnerId, dateRange, incidentViewMode } =
          values as any;

        const showLoading = !(formik as any).__skipLoading;
        const forceRefresh = Boolean((formik as any).__forceRefresh);
        const data = await fetchIncidentsData(
          branch,
          date,
          partnerId ?? null,
          dateRange,
          showLoading,
          undefined,
          incidentViewMode || "pending",
          forceRefresh
        );
        (formik as any).__forceRefresh = false;

        const validRecords = data.records.filter(
          (r) => r && r.status && r.schedule?.id
        );
        const validUnschedule = data.unschedule.filter(
          (u) => u && u.partner?.id && u.checkIn?.id
        );

        const seenIncidentKeys = new Set<string>();
        const finalRecords = validRecords.filter((r) => {
          const key = `${r.schedule.id}-${r.status}`;
          if (seenIncidentKeys.has(key)) return false;
          seenIncidentKeys.add(key);
          return true;
        });

        const seenUnscheduleKeys = new Set<string>();
        const finalUnschedule = validUnschedule.filter((u) => {
          const key = `${u.partner.id}-${u.checkIn.id}`;
          if (seenUnscheduleKeys.has(key)) return false;
          seenUnscheduleKeys.add(key);
          return true;
        });

        console.log(
          "✅ DATOS RECIBIDOS DESPUÉS DE FETCH (con filtrado de duplicados):",
          {
            recordsRecibidos: data.records.length,
            recordsValidos: validRecords.length,
            recordsFinales: finalRecords.length,
            unscheduleRecibidos: data.unschedule.length,
            unscheduleValidos: validUnschedule.length,
            unscheduleFinales: finalUnschedule.length,
          }
        );

        setRecords(finalRecords);
        setUnschedule(finalUnschedule);
        setDayClose((data as any).dayClose || null);
        (formik as any).__skipLoading = false;
      } catch (error) {
        console.log(error);
        setLoading(false);
        (formik as any).__skipLoading = false;
      } finally {
        setSubmitting(false);
      }
    },
  });

  const removeRecord = React.useCallback((scheduleId: number) => {
    setRecords((prev) =>
      prev.filter((record) => record.schedule.id !== scheduleId)
    );
  }, []);

  const reapplyIncidentViewMode = React.useCallback(
    (mode: "pending" | "resolved" | "all") => {
      const { branch, date, partnerId, dateRange } = formik.values as any;
      if (!canFetchIncidentsValues({ branch, date }, isDirectBossAccess, bossId)) {
        return false;
      }
      const rawKey = buildIncidentsRawCacheKey({
        isDirectBossAccess,
        bossId,
        directBossId: isDirectBossAccess ? Number(bossId) : undefined,
        branch,
        date,
        partnerId: partnerId ?? null,
        dateRange,
      });
      const raw = rawCacheRef.current.get(rawKey);
      if (!raw) return false;
      const applied = applyIncidentViewMode(raw, mode);
      setRecords(applied.records);
      setUnschedule(applied.unschedule);
      return true;
    },
    [formik.values, isDirectBossAccess, bossId]
  );

  const removeUnscheduleRecord = React.useCallback(
    (partnerId: number, checkInId: number) => {
      setUnschedule((prev) =>
        prev.filter(
          (record) =>
            !(
              record.partner.id === partnerId && record.checkIn.id === checkInId
            )
        )
      );
    },
    []
  );

  const fetchDependencies = async () => {
    try {
      const branches = await readBranches();
      setBranchs(branches);
      if (isDirectBossAccess) {
        try {
          const list = bossId
            ? await fetchDirectBossesByBossId(Number(bossId))
            : [];
          const directBossData =
            Array.isArray(list) && list.length > 0 ? list[0] : null;
          setDirectBossEmployees(directBossData?.employees || []);
        } catch (err) {
          console.log("Error al obtener empleados de jefe directo", err);
          setDirectBossEmployees([]);
        }
        const branchId = partner?.candidate?.branch?.id
          ? Number(partner.candidate.branch.id)
          : user?.branchId
          ? Number(user.branchId)
          : branches[0]?.id;
        if (!branchId) {
          if (!isDirectBossAccess || !bossId) {
            return;
          }
        } else {
          const branch = branches.find(
            (branch: Branch) => Number(branch.id) === branchId
          );
          setTitle(
            `Incidencias de ${
              branch?.name || partner?.candidate?.branch?.name || branchId
            }`
          );
          await formik.setFieldValue("branch", branchId);
        }
      } else {
        const leaderBranchId = user?.rol === "Lider de sucursal" ? Number(user?.branchId || partner?.candidate?.branch?.id) : null;
        if (leaderBranchId) {
          setTitle(`Incidencias de ${branches.find((branch: Branch) => Number(branch.id) === leaderBranchId)?.name || leaderBranchId}`);
          // Verificar si el líder también es jefe directo antes de disparar el fetch.
          // partner puede ser null en el estado de auth (login con email no lo incluye),
          // así que si no está, lo obtenemos del endpoint de usuario.
          try {
            const resolved = await resolveLeaderPartnerId(leaderBranchId);
            let leaderPartnerId = resolved.partnerId;
            let bossList: any[] = [];

            if (leaderPartnerId) {
              bossList = await fetchDirectBossesByBossId(leaderPartnerId);
            } else {
              // Fallback: cuando el usuario líder no tiene partnerId, buscar su configuración
              // de jefe directo por nombre normalizado en el módulo direct-boss.
              const searchName = [user?.name, user?.lastName, user?.secondLastName]
                .filter(Boolean)
                .join(" ")
                .trim();
              if (searchName) {
                const directBossResp = await fetchDirectBosses(
                  `?limit=25&offset=0&search=${encodeURIComponent(searchName)}`
                );
                const possible = (directBossResp?.results || []).filter((row: any) => {
                  const bFirst = normalizeText(row?.boss?.person?.firstName);
                  const bLast = normalizeText(row?.boss?.person?.lastName);
                  const bSecond = normalizeText(row?.boss?.person?.secondLastName);
                  const bFull = [bFirst, bLast, bSecond].filter(Boolean).join(" ").trim();
                  const uFull = [
                    normalizeText(user?.name),
                    normalizeText(user?.lastName),
                    normalizeText(user?.secondLastName),
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .trim();
                  return bFull === uFull;
                });
                if (possible.length === 1) {
                  leaderPartnerId = Number(possible[0]?.boss?.id);
                  bossList = await fetchDirectBossesByBossId(leaderPartnerId);
                  console.log(
                    "Líder asociado a direct-boss por nombre normalizado (fallback):",
                    {
                      userId: user?.id,
                      leaderPartnerId,
                    }
                  );
                } else if (possible.length > 1) {
                  console.warn(
                    "Múltiples coincidencias en direct-boss por nombre para líder; no se autoasigna.",
                    { userId: user?.id, matches: possible.map((p: any) => p?.boss?.id) }
                  );
                }
              }
            }

            if (
              leaderPartnerId &&
              Array.isArray(bossList) &&
              bossList.length > 0 &&
              bossList[0]?.employees?.length > 0
            ) {
              leaderDirectBossPartnerIdRef.current = leaderPartnerId;
              const employees = bossList[0].employees || [];

              leaderDirectBossEmployeeIdsRef.current = employees
                .map((emp: any) => Number(emp?.id))
                .filter((id: number) => Boolean(id));
              leaderBlockedEmployeeIdsRef.current =
                await calculateBlockedEmployeesForLeader(
                  leaderPartnerId,
                  employees.map((emp: any) => Number(emp?.id))
                );

              if (resolved.matchedByName) {
                console.log(
                  "Líder asociado a partner por nombre normalizado para jefe directo:",
                  {
                    userId: user?.id,
                    leaderPartnerId,
                  }
                );
              }
            } else {
              leaderDirectBossPartnerIdRef.current = null;
              leaderDirectBossEmployeeIdsRef.current = [];
              leaderBlockedEmployeeIdsRef.current =
                await calculateBlockedEmployeesForLeader(null, []);
            }
          } catch {
            leaderDirectBossPartnerIdRef.current = null;
            leaderDirectBossEmployeeIdsRef.current = [];
            leaderBlockedEmployeeIdsRef.current =
              await calculateBlockedEmployeesForLeader(null, []);
          }
          await formik.setFieldValue("branch", leaderBranchId);
          return;
        }

        if (id) {
          const number = Number(id);
          const branch = branches.find(
            (branch: Branch) => Number(branch.id) === number
          );
          if (branch) {
            setTitle(`Incidencias de ${branch.name}`);
            await formik.setFieldValue("branch", branch.id);
          }
        } else {
          setTitle("Incidencias");
          await formik.setFieldValue("branch", -99);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateCheckout = (checkout: Check, index: number) => {
    setRecords((prev) =>
      prev.map((record, i) =>
        i === index ? { ...record, checkOut: checkout } : record
      )
    );
  };

  const updateIncidentRecord = React.useCallback(
    (scheduleId: number, updates: { type?: string; reason?: string }) => {
      setRecords((prev) =>
        prev.map((record) => {
          if (record.schedule.id === scheduleId) {
            const updated = { ...record };
            if (updates.type) {
              (updated as any).type = updates.type;
            }
            if (updates.reason !== undefined) {
              (updated as any).reason = updates.reason;
            }

            // Actualizar también el cache local
            const { branch, date, partnerId, dateRange, incidentViewMode } =
              formik.values as any;
            const hasRange = Boolean(
              dateRange?.startDate && dateRange?.endDate
            );
            const cacheKey = isDirectBossAccess
              ? `directboss-${bossId || "none"}-${
                  hasRange && dateRange
                    ? `${dateRange.startDate}-${dateRange.endDate}`
                    : date
                }-${partnerId || "all"}-mode:${incidentViewMode || "pending"}`
              : hasRange && dateRange
              ? `${branch}-${dateRange.startDate}-${dateRange.endDate}-${
                  partnerId || "all"
                }-mode:${incidentViewMode || "pending"}`
              : `${branch}-${date}-${partnerId || "all"}-mode:${
                  incidentViewMode || "pending"
                }`;

            const cachedData = cacheRef.current.get(cacheKey);
            if (cachedData) {
              const updatedCache = {
                records: cachedData.records.map((r: Incident) =>
                  r.schedule.id === scheduleId ? updated : r
                ),
                unschedule: cachedData.unschedule,
              };
              cacheRef.current.set(cacheKey, updatedCache);
            }

            return updated;
          }
          return record;
        })
      );
    },
    [formik.values, isDirectBossAccess, bossId]
  );

  React.useEffect(() => {
    if (formik.values.branch !== -99) {
      const branch = branchs.find(
        (branch: Branch) => Number(branch.id) === Number(formik.values.branch)
      ) as any;
      if (branch?.name) setTitle(`Incidencias de ${branch?.name}`);
    }
  }, [formik.values.branch]);

  React.useEffect(() => {
    const filterBranchId = Number(formik.values.branch);
    const branchIds = collectIncidentBranchIds(
      records,
      unschedule,
      filterBranchId > 0 && filterBranchId !== -99 ? [filterBranchId] : []
    );
    if (branchIds.length === 0) {
      setBranchMetaMap({});
      if (!isDirectBossAccess) {
        setHidePlannedColumns(false);
      }
      return;
    }

    let isMounted = true;
    loadBranchReportMetaMap(branchIds).then((map) => {
      if (!isMounted) return;
      setBranchMetaMap(map);
      if (!isDirectBossAccess) {
        const nightContext = resolveIncidentsNightTableContext(
          records,
          map,
          "branch",
          filterBranchId > 0 && filterBranchId !== -99
            ? filterBranchId
            : undefined
        );
        setHidePlannedColumns(nightContext.hidePlannedColumns);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [records, unschedule, formik.values.branch, isDirectBossAccess]);

  React.useEffect(() => {
    if (user) {
      fetchDependencies();
    }
    if (!isDirectBossAccess) {
      setDirectBossEmployees([]);
    }
  }, [user, bossId, isDirectBossAccess, partner?.id]);

  const pendingSaveRef = React.useRef(false);

  const handleAfterSave = React.useCallback(
    async (payload?: IncidentAfterSavePayload | number) => {
      const parsed = normalizeAfterSavePayload(payload);
      if (!parsed) return;

      pendingSaveRef.current = true;
      const { branch, date, partnerId, dateRange, incidentViewMode } =
        formik.values as any;
      const mode = (incidentViewMode || "pending") as
        | "pending"
        | "resolved"
        | "all";
      const rawKey = buildIncidentsRawCacheKey({
        isDirectBossAccess,
        bossId,
        directBossId: isDirectBossAccess ? Number(bossId) : undefined,
        branch,
        date,
        partnerId: partnerId ?? null,
        dateRange,
      });

      try {
        if (parsed.kind === "unschedule") {
          const dropUnschedule = (list: Unschedule[]) =>
            list.filter(
              (np) => Number(np.checkIn?.id) !== Number(parsed.checkInId)
            );

          const raw = rawCacheRef.current.get(rawKey);
          if (raw) {
            raw.filteredUnschedule = dropUnschedule(raw.filteredUnschedule);
            rawCacheRef.current.set(rawKey, raw);
          }
          setUnschedule((prev) => dropUnschedule(prev));

          if (parsed.newScheduleId) {
            const savedRows = await findIncidentsBySchedule(parsed.newScheduleId);
            const scheduleTemplate =
              savedRows?.[0]?.schedule ??
              buildUnscheduleIncidentRecord(parsed.unschedule!, date)?.schedule ??
              null;
            const mappedSaved = (savedRows || [])
              .map((savedInc: any) =>
                mapSavedIncidentToTableRow(
                  savedInc,
                  scheduleTemplate ?? savedInc?.schedule
                )
              )
              .filter((inc): inc is Incident => inc !== null);

            if (raw) {
              raw.savedResolvedIncidents = mergeIncidentsByKey(
                raw.savedResolvedIncidents,
                mappedSaved.filter(
                  (inc) =>
                    isResolvedOperationalIncident(inc) &&
                    !isPendingLeaderReviewIncident(inc)
                )
              );
              raw.savedOpenIncidents = mergeIncidentsByKey(
                raw.savedOpenIncidents.filter(
                  (inc) =>
                    !(
                      isResolvedOperationalIncident(inc) &&
                      !isPendingLeaderReviewIncident(inc)
                    )
                ),
                mappedSaved.filter(
                  (inc) =>
                    !isResolvedOperationalIncident(inc) &&
                    !isPendingLeaderReviewIncident(inc)
                )
              );
              // No inyectar findPendingIncidentsBySchedule: regeneraba
              // «No tuvo chequeo de entrada/salida» como filas «nuevas».
              rawCacheRef.current.set(rawKey, raw);
              const applied = applyIncidentViewMode(
                rawCacheRef.current.get(rawKey)!,
                mode
              );
              setRecords(applied.records);
              setUnschedule(applied.unschedule);
            } else {
              setUnschedule((prev) => dropUnschedule(prev));
            }
          } else if (raw) {
            const applied = applyIncidentViewMode(raw, mode);
            setRecords(applied.records);
            setUnschedule(applied.unschedule);
          }
        } else {
          const savedKey = `${parsed.scheduleId}-${parsed.status}`;
          const dropKey = (list: Incident[]) =>
            list.filter((inc) => incidentRowKey(inc) !== savedKey);

          setRecords((prev) => dropKey(prev));

          const raw = rawCacheRef.current.get(rawKey);
          const scheduleTemplate = parsed.record?.schedule ?? null;

          if (raw) {
            let nextRaw: IncidentsRawPayload = {
              ...raw,
              filteredIncidents: dropKey(raw.filteredIncidents),
              savedOpenIncidents: dropKey(raw.savedOpenIncidents),
              savedResolvedIncidents: dropKey(raw.savedResolvedIncidents),
            };

            const savedRows = await findIncidentsBySchedule(
              parsed.scheduleId
            );
            const dropSchedule = (list: Incident[]) =>
              list.filter((inc) => inc.schedule?.id !== parsed.scheduleId);

            const mapped = (savedRows || [])
              .map((savedInc: any) =>
                mapSavedIncidentToTableRow(
                  savedInc,
                  scheduleTemplate ?? savedInc?.schedule
                )
              )
              .filter((inc): inc is Incident => inc !== null);

            nextRaw = {
              ...nextRaw,
              savedResolvedIncidents: mergeIncidentsByKey(
                dropSchedule(nextRaw.savedResolvedIncidents),
                mapped.filter(
                  (inc) =>
                    isResolvedOperationalIncident(inc) &&
                    !isPendingLeaderReviewIncident(inc)
                )
              ),
              savedOpenIncidents: mergeIncidentsByKey(
                dropSchedule(nextRaw.savedOpenIncidents),
                mapped.filter(
                  (inc) =>
                    !isResolvedOperationalIncident(inc) &&
                    !isPendingLeaderReviewIncident(inc)
                )
              ),
            };

            rawCacheRef.current.set(rawKey, nextRaw);
            const applied = applyIncidentViewMode(
              rawCacheRef.current.get(rawKey)!,
              mode
            );
            setRecords(applied.records);
            setUnschedule(applied.unschedule);
          }
        }

        const applyDayClose = (dayCloseValue: any) => {
          if (!dayCloseValue) return;
          setDayClose(dayCloseValue);
          const rawAfter = rawCacheRef.current.get(rawKey);
          if (rawAfter) {
            rawAfter.dayClose = dayCloseValue;
            rawCacheRef.current.set(rawKey, rawAfter);
          }
        };

        if (parsed.dayClose) {
          applyDayClose(parsed.dayClose);
        } else {
          // Si ya no quedan pendientes en pantalla, mostrar cierre de inmediato
          // (el backend ya cerró el día anterior; evita esperar tab/refresh).
          const rawAfter = rawCacheRef.current.get(rawKey);
          const pendingView = rawAfter
            ? applyIncidentViewMode(rawAfter, "pending")
            : { records: [], unschedule: [] };
          if (
            pendingView.records.length === 0 &&
            pendingView.unschedule.length === 0
          ) {
            const operationDate =
              typeof date === "string" && date
                ? date
                : dayjs().format("YYYY-MM-DD");
            const today = dayjs().format("YYYY-MM-DD");
            if (operationDate < today) {
              applyDayClose({
                date: operationDate,
                closedAt: new Date().toISOString(),
              });
            }
          }
        }

        for (const key of cacheRef.current.keys()) {
          if (key.startsWith(rawKey)) {
            cacheRef.current.delete(key);
          }
        }

        clearProfileImageCacheByCurp();
        notification("Éxito", "Incidencias guardadas correctamente", "success");
      } catch (error) {
        console.error("Error al actualizar incidencias tras guardar:", error);
        notification(
          "Error",
          "No se pudieron actualizar las incidencias en pantalla",
          "danger"
        );
      } finally {
        pendingSaveRef.current = false;
      }
    },
    [formik, isDirectBossAccess, bossId]
  );

  React.useEffect(() => {
    if (pendingSaveRef.current) {
      return;
    }
    const { branch, date } = formik.values;
    if (!canFetchIncidentsValues({ branch, date }, isDirectBossAccess, bossId)) {
      return;
    }
    clearProfileImageCacheByCurp();
    (formik as any).__forceRefresh = true;
    formik.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.branch, refetch, bossId, isDirectBossAccess]);

  React.useEffect(() => {
    if (pendingSaveRef.current) {
      return;
    }
    const { branch, date } = formik.values;
    if (!canFetchIncidentsValues({ branch, date }, isDirectBossAccess, bossId)) {
      return;
    }
    if (isInitialDateRef.current) {
      isInitialDateRef.current = false;
      return;
    }
    clearProfileImageCacheByCurp();
    clearIncidentsCache();
    formik.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.date, clearIncidentsCache]);

  React.useEffect(() => {
    if (pendingSaveRef.current) {
      lastPartnerIdRef.current = formik.values.partnerId;
      return;
    }

    const { branch, date } = formik.values;
    if (!canFetchIncidentsValues({ branch, date }, isDirectBossAccess, bossId)) {
      lastPartnerIdRef.current = formik.values.partnerId;
      return;
    }

    const hasRange = Boolean(
      formik.values?.dateRange?.startDate && formik.values?.dateRange?.endDate
    );
    const partnerChanged = formik.values.partnerId !== lastPartnerIdRef.current;

    if (hasRange || formik.values.partnerId || partnerChanged) {
      clearProfileImageCacheByCurp();
      clearIncidentsCache();
      formik.handleSubmit();
    }

    lastPartnerIdRef.current = formik.values.partnerId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.dateRange, formik.values.partnerId, clearIncidentsCache]);

  React.useEffect(() => {
    if (pendingSaveRef.current) {
      return;
    }
    const mode = formik.values.incidentViewMode || "pending";
    if (prevIncidentViewModeRef.current === undefined) {
      prevIncidentViewModeRef.current = mode;
      return;
    }
    if (prevIncidentViewModeRef.current === mode) {
      return;
    }
    prevIncidentViewModeRef.current = mode;

    const { branch, date } = formik.values;
    if (!canFetchIncidentsValues({ branch, date }, isDirectBossAccess, bossId)) {
      return;
    }
    if (mode === "all" || mode === "resolved") {
      const { branch, date, partnerId, dateRange } = formik.values as any;
      if (!canFetchIncidentsValues({ branch, date }, isDirectBossAccess, bossId)) {
        return;
      }
      invalidateIncidentsCache(
        branch,
        date,
        partnerId ?? null,
        dateRange
      );
      setLoading(true);
      void fetchIncidentsData(
        branch,
        date,
        partnerId ?? null,
        dateRange,
        false,
        undefined,
        mode
      )
        .then((data) => {
          setRecords(data.records);
          setUnschedule(data.unschedule);
          setDayClose((data as any).dayClose || null);
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }
    if (!reapplyIncidentViewMode(mode)) {
      (formik as any).__skipLoading = true;
      formik.handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.incidentViewMode]);

  React.useEffect(() => {
    return () => {
      clearIncidentsCache();
    };
  }, [clearIncidentsCache]);

  const FormComponent = isDirectBossAccess
    ? IncidentsFormDirectBoss
    : IncidentsForm;
  const TableComponent: React.FC<IncidentsTableProps> = isDirectBossAccess
    ? IncidentsDirectBoss
    : Incidents;
  const pageTitle = isDirectBossAccess ? "Incidencias de jefe directo" : title;

  return (
    <React.Fragment>
      <Layout loading={false}>
        <Title
          baseTitle={
            isDirectBossAccess ? "Incidencias de jefe directo" : "Sucursales"
          }
          basePath={isDirectBossAccess ? "/incidencias" : "/sucursales"}
          activeTitle={pageTitle}
          title={pageTitle}
        />

        <div
          className="mx-2 mx-md-4 mb-3 mb-md-4 incidents-page-form-card"
          style={{
            borderRadius: "16px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            background: "white",
          }}
        >
          <Panel>
            <PanelBody className="p-3 p-md-4">
              <FormComponent
                formik={formik}
                branches={branchs}
                disabled={
                  user?.rol === "Lider de sucursal" || isDirectBossAccess
                }
                setLoading={() => {}}
                hideBranchAutocomplete={isDirectBossAccess}
                branchId={
                  isDirectBossAccess && partner?.candidate?.branch?.id
                    ? Number(partner.candidate.branch.id)
                    : undefined
                }
                directBossEmployees={
                  isDirectBossAccess ? directBossEmployees : undefined
                }
              />
            </PanelBody>
          </Panel>
        </div>

        <div className="mx-2 mx-md-4 mb-2 mb-md-4 incidents-page-table-block">
          <TableComponent
            records={records}
            unschedule={unschedule}
            removeRecord={removeRecord}
            removeUnscheduleRecord={removeUnscheduleRecord}
            onAfterSave={handleAfterSave}
            loading={loading}
            updateIncidentRecord={updateIncidentRecord}
            incidentViewMode={formik.values.incidentViewMode || "pending"}
            dayClose={dayClose}
            onViewModeChange={async (mode) => {
              prevIncidentViewModeRef.current = mode;
              await formik.setFieldValue("incidentViewMode", mode);
              const { branch, date, partnerId, dateRange } =
                formik.values as any;
              if (mode === "all" || mode === "resolved") {
                if (
                  !canFetchIncidentsValues(
                    { branch, date },
                    isDirectBossAccess,
                    bossId
                  )
                ) {
                  return;
                }
                invalidateIncidentsCache(
                  branch,
                  date,
                  partnerId ?? null,
                  dateRange
                );
                setLoading(true);
                try {
                  const data = await fetchIncidentsData(
                    branch,
                    date,
                    partnerId ?? null,
                    dateRange,
                    false,
                    undefined,
                    mode
                  );
                  setRecords(data.records);
                  setUnschedule(data.unschedule);
                  setDayClose((data as any).dayClose || null);
                } finally {
                  setLoading(false);
                }
                return;
              }
              if (!reapplyIncidentViewMode(mode)) {
                (formik as any).__skipLoading = true;
                formik.handleSubmit();
              }
            }}
            isAdmin={isAdmin}
            hidePlannedColumns={hidePlannedColumns}
            branchMetaMap={branchMetaMap}
            showBranchName={isDirectBossAccess}
            branchNameById={branchNameById}
            operationDate={
              formik.values.dateRange?.startDate &&
              formik.values.dateRange?.endDate
                ? undefined
                : formik.values.date
            }
          />
        </div>
      </Layout>
      <ModalContainer
        open={showModal}
        title="Crear salida"
        children={
          <ModalClock
            close={() => dispatch(setShowModal(false))}
            records={records}
            updateCheckout={updateCheckout}
          />
        }
        closeFn={() => dispatch(setShowModal(false))}
      />
    </React.Fragment>
  );
};

export default TableIncidents;
